/**
 * ⟦A-15 §3⟧ / **M3.16** — **Les créateurs PNJ persistants : un rival qui monte pendant qu'on
 * dort.**
 *
 * A-15 la désigne comme *« la pression la plus forte psychologiquement, et la seule qui donne
 * un visage au temps perdu »*. C'est exact, et c'est aussi pourquoi elle est la plus facile à
 * rater : une jauge qui descend est une contrainte, un type qui te dépasse est une histoire.
 *
 * M3.16 ⟦A-4⟧ pose le cahier des charges : *« 2-3 PNJ par run (nom, niche, courbe simulée,
 * ton), portés par l'état, tirés au bootstrap ; la relation rival / bande de potes se
 * construit selon les réponses du joueur ; ignorer les PNJ reste jouable. Courbe simulée, pas
 * une run parallèle. »* A-15 §3 y ajoute une seule chose, et c'est la bonne : **le relier au
 * temps qui passe.**
 *
 * ── « Courbe simulée, pas une run parallèle » — ce que ça impose ────────────────
 *
 * La courbe est une **forme close** : `abonnesRival(rival, clock_h)`. Aucune accumulation,
 * aucun état à faire avancer tour par tour, donc **aucune dérive possible** — deux chemins qui
 * arrivent au même `clock_h` donnent le même nombre, toujours. C'est aussi ce qui rend la
 * comparaison « où en était-il avant mon evergreen de 4 jours ? » gratuite : on évalue la
 * fonction deux fois.
 *
 * Le modèle est **logistique** : `K / (1 + ((K − N₀)/N₀)·e^(−r·t))`. Il dit exactement ce
 * qu'on veut raconter — ça démarre lentement, ça s'emballe, puis ça plafonne. Tous les PNJ ne
 * deviennent pas des stars, et c'est le plafond qui le dit.
 *
 * ⚠️ Le temps du jeu **n'avance que quand le joueur agit** (PLAN §4.1) : le rival progresse
 * donc en fonction des heures que le joueur DÉPENSE, pas d'une horloge réelle. C'est
 * précisément la pression voulue — « ton evergreen t'a coûté quatre jours, il en a profité » —
 * et c'est ce qui garde le moteur pur (aucune horloge système, CLAUDE.md).
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * **Les PNJ n'ont AUCUN effet mécanique sur les jauges du joueur.** Pas un abonné, pas un
 * point de réputation. Ce n'est pas une timidité : c'est le dessin. La pression est un
 * **miroir**, et un miroir ne pousse pas. Trois conséquences directes :
 *
 *  - aucune ancre de calibrage ne peut bouger, quoi qu'il arrive à un PNJ ;
 *  - « ignorer les PNJ reste jouable » (M3.16) est vrai **par construction**, pas par réglage ;
 *  - `state.rivaux` est optionnel — `undefined` ⇔ aucun PNJ ⇔ le jeu d'avant l'avenant, et
 *    `content/rivaux.json` absent produit exactement ça.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random` — le tirage
 * passe par `draw(seed, …, "pnj_pick")`.
 */
import { H_JOUR } from "./clock.js";
import { draw, uniformInt } from "./rng.js";
/**
 * Extrait les PNJ d'un contenu qui les porte. Contenu sans `rivaux` ⇒ vivier vide ⇒ aucun PNJ
 * tiré : l'absence est **visible dans les tests** plutôt que silencieusement permissive (même
 * shim que `evenementsDuContenu`).
 */
export function rivauxDuContenu(content) {
    const porteur = content;
    return porteur.rivaux ?? [];
}
/** La définition de contenu d'un PNJ porté par l'état — `undefined` si le contenu a changé. */
export function rivalDuContenu(content, id) {
    return rivauxDuContenu(content).find((r) => r.id === id);
}
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ✎ Nombre de PNJ par run : **2 minimum, 3 maximum** (M3.16 : « 2-3 PNJ par run »). Le
 * nombre exact est tiré, pour que deux runs ne se ressemblent pas.
 */
export const RIVAUX_MIN = 2;
export const RIVAUX_MAX = 3;
/**
 * ✎ Écart d'abonnés en dessous duquel on considère qu'on est **au coude à coude** — 10 %.
 * C'est le seuil qui décide du ton du message : « il te double » n'a pas de sens à 3 abonnés
 * d'écart, et « tu es loin derrière » en a trop à 10 000.
 */
export const SEUIL_COUDE_A_COUDE = 0.1; // ✎
/** Axe relationnel de M3.16 — de la rivalité franche à la bande de potes. Neutre à 0. */
export const RELATION_MIN = -100;
export const RELATION_MAX = 100;
/** Bandes lisibles de l'axe relationnel ✎ (±40, l'échelle des bandes d'alignement A23). */
export const SEUIL_RIVAL = -40;
export const SEUIL_POTE = 40;
export function bandeRelation(relation) {
    if (relation <= SEUIL_RIVAL)
        return "rival";
    if (relation >= SEUIL_POTE)
        return "pote";
    return "connaissance";
}
// ─────────────────────────────────────────────────────────────────────────────
// La courbe — forme close, jamais une accumulation
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Abonnés d'un PNJ à un instant donné (heures éveillées). **Forme close** : deux appels au
 * même `clockH` rendent toujours le même nombre, et aucune dérive ne peut s'installer.
 *
 * Logistique `K / (1 + ((K − N₀)/N₀)·e^(−r·t))`, `t` en **jours simulés**.
 *
 * ⚠️ **`offset` vaut 0 dans TOUTES les parties, et rien ne peut le changer aujourd'hui.** Il est
 * lu ici, initialisé à 0 par `tirerRivaux`, et **aucun écrivain n'existe dans le dépôt**
 * ⟦audit des quatre maillons, 19/08, rupture 8⟧. La courbe d'un PNJ est donc entièrement
 * déterminée par `(graine, heure)` : ni buzz, ni strike, ni drama, ni collab ne l'infléchit
 * jamais d'un abonné. **Le rival est un métronome.**
 *
 * Ce n'est pas un oubli d'une ligne, et c'est pour ça que le champ reste plutôt que de
 * disparaître — deux verrous le tiennent, tous deux volontaires :
 *  - les 18 types d'effets de SPEC-CONTENU §2.3 sont un enum **fermé** (`discriminatedUnion` +
 *    `.strict()`), donc aucun événement de `content/` ne peut viser un PNJ. Il faudrait un 19e
 *    type — exactement le blocage déjà documenté sur `appliquerRelationRival` ci-dessous ;
 *  - et le rendre vivant **rend les parties moins comparables**, ce qui complique tout le
 *    calibrage : deux runs de même graine ne se répondraient plus.
 *
 * ✎ **C'est donc un arbitrage de Mathieu, pas une réparation.** S'il le tranche « vivant », la
 * forme à respecter est l'interrupteur de contenu d'⟦A-57⟧ (`meta.fil_actif`) : par défaut
 * **éteint**, donc état neutre, donc les mesures d'aujourd'hui restent valides.
 */
export function abonnesRival(rival, clockH) {
    const t = Math.max(0, clockH) / H_JOUR;
    const n0 = Math.max(1, rival.depart);
    const k = Math.max(n0, rival.plafond);
    const brut = k / (1 + ((k - n0) / n0) * Math.exp(-rival.vitesse * t));
    return Math.max(0, Math.round(brut + rival.offset));
}
/** Ce que le PNJ a gagné entre deux instants — la lecture qu'⟦A-16⟧ met dans le récapitulatif. */
export function progressionRival(rival, deH, aH) {
    return abonnesRival(rival, aH) - abonnesRival(rival, deH);
}
// ─────────────────────────────────────────────────────────────────────────────
// Le tirage au bootstrap
// ─────────────────────────────────────────────────────────────────────────────
/** Tire une valeur dans une fourchette de contenu, sur un `u` déjà consommé. */
function dansLaFourchette(u, [min, max]) {
    return min + u * (max - min);
}
/**
 * Tire les 2-3 PNJ de la run. MUTE `state`. **Déterministe** : mêmes `(contenu, seed)` ⇒
 * mêmes PNJ, mêmes paramètres — c'est le critère d'acceptation de M3.16.
 *
 * Tirage **sans remise** : deux PNJ identiques dans une même run n'auraient aucun sens.
 * Purpose `pnj_pick`, index = rang du tirage.
 *
 * Vivier vide (contenu sans `rivaux.json`) ⇒ `state.rivaux` reste `undefined` : l'état neutre,
 * et pas un tableau vide qui prétendrait que la mécanique tourne.
 */
export function tirerRivaux(state, content, seed) {
    const vivier = rivauxDuContenu(content);
    if (vivier.length === 0)
        return [];
    const uNombre = draw(seed, state.turn, "pnj_pick", 0);
    const combien = Math.min(vivier.length, uniformInt(uNombre, RIVAUX_MIN, RIVAUX_MAX));
    const restants = [...vivier];
    const tires = [];
    for (let i = 0; i < combien && restants.length > 0; i++) {
        const u = draw(seed, state.turn, "pnj_pick", i + 1);
        const [choisi] = restants.splice(uniformInt(u, 0, restants.length - 1), 1);
        if (!choisi)
            break;
        // Trois tirages de paramètres par PNJ, sur des index qui ne peuvent pas entrer en
        // collision avec la sélection ci-dessus (décalage de 100, comme `piocherCartes`).
        const uDepart = draw(seed, state.turn, "pnj_pick", 100 + i * 3);
        const uVitesse = draw(seed, state.turn, "pnj_pick", 101 + i * 3);
        const uPlafond = draw(seed, state.turn, "pnj_pick", 102 + i * 3);
        tires.push({
            id: choisi.id,
            depart: Math.round(dansLaFourchette(uDepart, choisi.depart)),
            vitesse: dansLaFourchette(uVitesse, choisi.vitesse),
            plafond: Math.round(dansLaFourchette(uPlafond, choisi.plafond)),
            relation: 0, // M3.16 : la relation se CONSTRUIT, elle ne se tire pas
            offset: 0,
            vu_a_h: 0,
        });
    }
    state.rivaux = tires;
    return tires;
}
// ─────────────────────────────────────────────────────────────────────────────
// Lectures — le miroir que le joueur regarde
// ─────────────────────────────────────────────────────────────────────────────
/** Les PNJ de la run — `undefined` ⇔ aucun (état neutre, jamais lu en direct). */
export function rivaux(state) {
    return state.rivaux ?? [];
}
/**
 * Les PNJ, annotés. **C'est toute la mécanique côté joueur** : on la regarde, elle ne fait
 * rien d'autre. Rend une liste vide si la run n'a pas de PNJ ou si le contenu ne les porte
 * plus (contenu remplacé sous une run en cours) — jamais une erreur.
 */
export function rivauxLisibles(state, content) {
    return rivaux(state).flatMap((r) => {
        const def = rivalDuContenu(content, r.id);
        if (!def)
            return [];
        const subs = abonnesRival(r, state.clock_h);
        const ecart = subs - state.subscribers;
        const echelle = Math.max(1, Math.max(subs, state.subscribers));
        return [
            {
                id: r.id,
                nom: def.nom,
                niche: def.niche,
                ton: def.ton,
                description: def.description,
                subscribers: subs,
                ecart,
                coude_a_coude: Math.abs(ecart) / echelle <= SEUIL_COUDE_A_COUDE,
                joueur_devant: ecart < 0,
                relation: r.relation,
                bande: bandeRelation(r.relation),
            },
        ];
    });
}
/**
 * ⚠️ **Il n'y a pas de notion de « visite », et `RivalEtat.vu_a_h` n'a plus aucun lecteur.**
 *
 * `marquerRivauxVus(state)` a vécu ici, avec un `gagnes_depuis_derniere_visite` dans
 * `LectureRival` en face. **Aucune des deux n'a jamais eu d'appelant de production**
 * ⟦audit des quatre maillons, 19/08⟧ : `vu_a_h` restait à 0 pour toute la partie, si bien que
 * « ce qu'il a pris depuis ta dernière visite » disait en réalité *« depuis le début de la
 * run »* — un champ qui mentait sur son propre nom, à chaque lecture.
 *
 * **Retirées plutôt que branchées, parce que la visite n'existe pas dans le dessin.** Le
 * panneau *Concurrence* n'est pas un écran qu'on ouvre : il est monté **en permanence** dans le
 * HUD du Studio, sous le compteur d'abonnés ⟦A-7 décision 4, « on ne quitte pas la chambre pour
 * consulter quelque chose »⟧. Le joueur ne « revient » jamais voir les PNJ — il les a sous les
 * yeux à chaque tour. Il n'y a donc aucun geste à marquer, et aucun endroit légitime où muter
 * l'état : `signalerProgressionRivaux` est explicitement un **pur observateur**, et le client
 * ne peut rien persister.
 *
 * **Et la question a déjà deux réponses justes, toutes deux sur une fenêtre EXPLICITE** — ce
 * que « depuis ta dernière visite » ne pouvait pas être :
 *  - le panneau affiche `progressionRival(r, clock_h − 24 h, clock_h)`, soit « +N/j » ;
 *  - le récapitulatif ⟦A-16⟧ et le `warning` `rival_progresse` disent ce qu'il a pris **pendant
 *    l'action que tu viens de faire** — « ton evergreen t'a coûté quatre jours, il en a
 *    profité », la phrase exacte de l'en-tête de ce module.
 *
 * ✎ Reliquat assumé : `vu_a_h` reste dans `RivalEtat` (`types.ts`), désormais **écrit une fois
 * à 0 et jamais relu**. Le retirer touche la forme de l'état sérialisé et `types.ts`, qui
 * appartient à une autre session — à faire par qui en a la charge, sans urgence.
 */
/**
 * ⟦M3.16⟧ Déplace l'axe relationnel d'un PNJ — *« la relation rival / bande de potes se
 * construit selon les réponses du joueur »*. MUTE `state` ; rend la nouvelle valeur, ou
 * `null` si le PNJ n'est pas dans cette run.
 *
 * ✅ ⟦A-84⟧ **Branchée.** L'écart signalé ici depuis M3.16 (« il faudra un type
 * `rival_relation_delta` — un vrai avenant, pas un ajout discret ») est refermé : c'est le
 * 22ᵉ type de SPEC-CONTENU §2.3, appliqué par `applyEffectsInPlace` (`effects.ts`) via
 * `ciblesRelation` ci-dessous, et raconté au journal par `narrerRelations`. Le contenu qui
 * l'utilise : les événements où « un créateur » intervient (`collab_proposee`,
 * `drama_createur`, `good_buzz_citation`…) — ce créateur-là est désormais un PNJ de la run.
 */
export function appliquerRelationRival(state, rivalId, delta) {
    const r = rivaux(state).find((x) => x.id === rivalId);
    if (!r)
        return null;
    r.relation = Math.max(RELATION_MIN, Math.min(RELATION_MAX, r.relation + delta));
    return r.relation;
}
/**
 * Les PNJ visés par un rôle, dans l'ordre du bootstrap (déterministe : mêmes PNJ, même
 * heure ⇒ même cible). Liste vide si la run n'a pas de PNJ — l'état neutre, rien à écrire.
 * `devant` / `derriere` retombent sur `plus_proche` quand personne ne tient le rôle : un
 * événement écrit pour « un grand te cite » ne doit pas devenir muet parce que le joueur est
 * déjà n°1.
 */
export function ciblesRelation(state, cible = "plus_proche") {
    const pnj = rivaux(state);
    if (pnj.length === 0)
        return [];
    if (cible === "tous")
        return [...pnj];
    const annotes = pnj.map((r, index) => ({
        r,
        index,
        ecart: abonnesRival(r, state.clock_h) - state.subscribers,
    }));
    const plusProche = (liste) => [...liste].sort((a, b) => Math.abs(a.ecart) - Math.abs(b.ecart) || a.index - b.index)[0].r;
    if (cible === "devant") {
        const devant = annotes.filter((a) => a.ecart > 0);
        if (devant.length > 0)
            return [plusProche(devant)];
    }
    if (cible === "derriere") {
        const derriere = annotes.filter((a) => a.ecart <= 0);
        if (derriere.length > 0)
            return [plusProche(derriere)];
    }
    return [plusProche(annotes)];
}
export function photoRelations(state) {
    return rivaux(state).map((r) => ({ id: r.id, relation: r.relation, bande: bandeRelation(r.relation) }));
}
/** Ce que le journal dit d'une bande, au moment où on y entre. */
const ENTREE_EN_BANDE = {
    rival: "c'est un rival, maintenant — et il le sait.",
    pote: "vous êtes potes, maintenant. Ça se verra dans vos vidéos.",
    connaissance: "vous êtes redevenus deux chaînes qui se croisent.",
};
/**
 * ⟦A-84⟧ **La relation se raconte au moment où elle bouge**, et elle se raconte avec un NOM :
 * « Un créateur de ta taille te propose une collab » est un texte générique ; la ligne de
 * journal qui suit le choix dit de qui il s'agissait. Une ligne par PNJ dont la valeur a
 * changé ; le changement de BANDE (±40) a sa phrase à lui, parce que c'est là que la
 * troisième voie de §7.2bis (« ni l'un ni l'autre ») cesse d'être la seule jouée.
 *
 * Pur : ne lit que l'état et le contenu, n'écrit rien. Un PNJ disparu du contenu (contenu
 * remplacé sous une run en cours) ne produit pas de ligne — jamais une erreur.
 */
export function narrerRelations(avant, state, content) {
    const lignes = [];
    for (const r of rivaux(state)) {
        const photo = avant.find((p) => p.id === r.id);
        if (!photo || photo.relation === r.relation)
            continue;
        const def = rivalDuContenu(content, r.id);
        if (!def)
            continue;
        const bande = bandeRelation(r.relation);
        if (bande !== photo.bande) {
            lignes.push(`Avec ${def.nom}, ça a basculé : ${ENTREE_EN_BANDE[bande]}`);
        }
        else {
            lignes.push(r.relation > photo.relation
                ? `Avec ${def.nom}, ça se réchauffe.`
                : `Avec ${def.nom}, ça se refroidit.`);
        }
    }
    return lignes;
}
// ─────────────────────────────────────────────────────────────────────────────
// Le lien au temps — « il progresse pendant que tu dors »
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ✎ Écart de progression à partir duquel on émet un `warning`. Un PNJ qui prend **20 abonnés**
 * pendant qu'on tourne mérite une ligne ; un qui en prend 2 n'est que du décor.
 */
export const SEUIL_ALERTE_RIVAL = 20; // ✎
/**
 * Émet ce que les PNJ ont gagné pendant l'intervalle `[deH ; aH]`. **N'écrit rien dans
 * l'état** : c'est un pur observateur, appelé par la file après l'avancée d'horloge.
 *
 * C'est le lien qu'A-15 §3 demande — *« il progresse pendant que le joueur dort »* — et il ne
 * peut pas être anxiogène : il ne retire rien, il raconte. Le joueur **regarde** un rival
 * monter, exactement le verbe employé par le garde-fou de l'avenant (« un rival se regarde
 * monter »).
 */
export function signalerProgressionRivaux(state, content, deH, aH, events = []) {
    if (aH <= deH)
        return [];
    const notables = [];
    for (const r of rivaux(state)) {
        const def = rivalDuContenu(content, r.id);
        if (!def)
            continue;
        const gagnes = progressionRival(r, deH, aH);
        if (gagnes < SEUIL_ALERTE_RIVAL)
            continue;
        notables.push({ id: r.id, nom: def.nom, gagnes });
        events.push({
            kind: "warning",
            data: {
                scripted: "rival_progresse",
                rival_id: r.id,
                nom: def.nom,
                gagnes,
                subscribers: abonnesRival(r, aH),
                ecart: abonnesRival(r, aH) - state.subscribers,
                relation: r.relation,
            },
        });
    }
    return notables;
}
//# sourceMappingURL=rivaux.js.map